import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/axios';

// ── 타입 정의 ──

export interface MyGoal {
  goalId: string;
  title: string;
  frameworkType: 'OKR' | 'KPI' | 'MIXED';
  progress: number;
  periodType: string;
  periodStart: string;
  periodEnd: string;
  status: string;
  departmentName: string;
}

export interface MyProject {
  projectId: string;
  name: string;
  key: string;
  projectType: string;
  myRole: string;
  myOpenIssueCount: number;
  lastActivityAt: string;
  isFavorite: boolean;
}

export interface FavoriteItem {
  id: string;
  targetType: string;
  targetId: string;
  name: string;
  createdAt: string;
  /** DOCUMENT 즐겨찾기: 라우트 /documents/:spaceId/:documentId 이동에 사용 */
  spaceId?: string | null;
}

export interface IssueItem {
  id: string;
  key: string;
  title: string;
  projectId: string;
  projectKey: string;
  projectName: string;
  dueDate: string | null;
}

export interface MyIssuesData {
  filter: string;
  summary: {
    todo: number;
    inProgress: number;
    inReview: number;
    done: number;
    overdue: number;
  };
  issues: {
    todo: IssueItem[];
    inProgress: IssueItem[];
    inReview: IssueItem[];
    done: IssueItem[];
    overdue: IssueItem[];
  };
}

export interface PendingReview {
  reviewId: string;
  prNumber: number;
  prTitle: string;
  prUrl: string;
  projectName: string;
  issueKey: string | null;
  requesterName: string;
  requestedAt: string;
}

export interface TodoSchedule {
  id: string;
  startTime: string;
  endTime: string;
  isAllDay: boolean;
  recurrence: string | null;
  remindAt: string | null;
}

export interface TodoItem {
  id: string;
  title: string;
  description: string | null;
  completed: boolean;
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  dueDate: string | null;
  dueTime: string | null;
  sortOrder: number;
  linkedIssueId: string | null;
  linkedIssue: { issueId: string; title: string } | null;
  schedule: TodoSchedule | null;
  // CHG-123: 채팅 메시지 출처 (REQ-V123-041/042)
  sourceMessageId?: string | null;
  sourceMessage?: {
    messageId: string;
    channelId: string | null;
    isDeleted: boolean;
    channel: {
      id: string;
      name: string | null;
      type: 'PROJECT' | 'USER' | 'DM';
      isArchived: boolean;
      projectId: string | null;
      projectKey: string | null;
    } | null;
  } | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTodoInput {
  title: string;
  description?: string;
  priority?: 'HIGH' | 'MEDIUM' | 'LOW';
  dueDate?: string;
  dueTime?: string;
  linkedIssueId?: string;
  schedule?: {
    startTime: string;
    endTime: string;
    isAllDay?: boolean;
    recurrence?: string | null;
    remindAt?: string | null;
  };
}

export interface UpdateTodoInput {
  title?: string;
  description?: string | null;
  completed?: boolean;
  priority?: 'HIGH' | 'MEDIUM' | 'LOW';
  dueDate?: string | null;
  dueTime?: string | null;
  linkedIssueId?: string | null;
  schedule?: {
    startTime: string;
    endTime: string;
    isAllDay?: boolean;
    recurrence?: string | null;
    remindAt?: string | null;
  } | null;
}

// ── 훅 ──

export function useMyGoals() {
  return useQuery({
    queryKey: ['my-home', 'goals'],
    queryFn: async () => {
      const { data } = await api.get<{ success: boolean; data: { goals: MyGoal[] } }>('/my-home/goals');
      return data.data.goals;
    },
    staleTime: 60_000,
  });
}

export function useMyProjects() {
  return useQuery({
    queryKey: ['my-home', 'projects'],
    queryFn: async () => {
      const { data } = await api.get<{ success: boolean; data: { projects: MyProject[] } }>('/my-home/projects');
      return data.data.projects;
    },
    staleTime: 60_000,
  });
}

export function useMyIssues(filter: 'assigned' | 'reported') {
  return useQuery({
    queryKey: ['my-home', 'issues', filter],
    queryFn: async () => {
      const { data } = await api.get<{ success: boolean; data: MyIssuesData }>(
        `/my-home/issues?filter=${filter}`,
      );
      return data.data;
    },
    staleTime: 60_000,
  });
}

export function useMyPendingReviews() {
  return useQuery({
    queryKey: ['my-home', 'pending-reviews'],
    queryFn: async () => {
      const { data } = await api.get<{ success: boolean; data: { pendingReviews: PendingReview[] } }>(
        '/my-home/pending-reviews',
      );
      return data.data.pendingReviews;
    },
    staleTime: 60_000,
  });
}

export function useMyFavorites(type: 'CHANNEL' | 'FILTER' | 'DOCUMENT' | 'IDEA') {
  return useQuery({
    queryKey: ['my-home', 'favorites', type],
    queryFn: async () => {
      const { data } = await api.get<{ success: boolean; data: { favorites: FavoriteItem[] } }>(
        `/my-home/favorites?type=${type}`,
      );
      return data.data.favorites;
    },
    staleTime: 60_000,
  });
}

// ── 즐겨찾기 토글 훅 ──

export function useAddFavorite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ targetType, targetId }: { targetType: string; targetId: string }) => {
      const { data } = await api.post('/my-home/favorites', { targetType, targetId });
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-home', 'favorites'] }),
  });
}

export function useRemoveFavorite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (favoriteId: string) => {
      await api.delete(`/my-home/favorites/${favoriteId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-home', 'favorites'] }),
  });
}

// ── To-do 훅 ──

const TODO_KEY = ['my-home', 'todos'];

export function useMyTodos(opts?: { view?: string; completed?: boolean; from?: string; to?: string }) {
  const params = new URLSearchParams();
  if (opts?.view) params.set('view', opts.view);
  if (opts?.completed !== undefined) params.set('completed', String(opts.completed));
  if (opts?.from) params.set('from', opts.from);
  if (opts?.to) params.set('to', opts.to);
  const qs = params.toString();

  return useQuery({
    queryKey: [...TODO_KEY, qs],
    queryFn: async () => {
      const { data } = await api.get<{ success: boolean; data: { todos: TodoItem[] } }>(
        `/my-home/todos${qs ? `?${qs}` : ''}`,
      );
      return data.data.todos;
    },
    staleTime: 30_000,
  });
}

export function useCreateTodo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateTodoInput) => {
      const { data } = await api.post('/my-home/todos', input);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TODO_KEY }),
  });
}

// CHG-123 REQ-V123-040: 채팅 메시지로부터 todo 생성
export interface CreateTodoFromMessageResult {
  id: string;
  title: string;
  sourceMessageId?: string | null;
}

export async function createTodoFromMessage(sourceMessageId: string): Promise<CreateTodoFromMessageResult> {
  const { data } = await api.post('/my-home/todos/from-message', { sourceMessageId });
  return data.data;
}

export function useUpdateTodo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ todoId, ...input }: UpdateTodoInput & { todoId: string }) => {
      const { data } = await api.patch(`/my-home/todos/${todoId}`, input);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TODO_KEY }),
  });
}

export function useDeleteTodo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (todoId: string) => {
      await api.delete(`/my-home/todos/${todoId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TODO_KEY }),
  });
}

export function useReorderTodos() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (orderedIds: string[]) => {
      await api.patch('/my-home/todos/reorder', { orderedIds });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TODO_KEY }),
  });
}
